// Simple modifier utilities
export const filterEnabledModifiers = (modifiers: any[] = []): any[] => {
  return modifiers
    .filter(modifier => 
      modifier.options && modifier.options.some((option: any) => option.isEnabled === true)
    )
    .map(modifier => ({
      ...modifier,
      options: modifier.options.filter((option: any) => option.isEnabled === true)
    }));
};

export const isModifierRequired = (modifier: any): boolean => {
  return modifier.is_forced?.toLowerCase() === 'yes';
};

export const isModifierMultiSelect = (modifier: any): boolean => {
  return modifier.is_multi_select?.toLowerCase() === 'yes';
};

export const isModifierOptionSelected = (
  modifierName: string,
  optionName: string,
  selectedModifiers: any[]
): boolean => {
  const modifier = selectedModifiers.find(mod => mod.name === modifierName);
  return modifier ? modifier.options.some((opt: any) => opt.name === optionName) : false;
};

export const handleModifierOptionSelection = (
  modifierName: string,
  option: any,
  selectedModifiers: any[],
  modifiersList: any[]
): any[] => {
  // SAFE: Auto-set "Whole" sub-option if option has sub-options
  let processedOption = option;
  if (option && 
      typeof option === 'object' && 
      hasSubOptions(option) && 
      !option.subOption) {
    try {
      // Auto-set "Whole" sub-option with main option price
      processedOption = {
        ...option,
        subOption: {
          selection: "Whole",
          price: option.price || '0'  // Safe fallback
        }
      };
    } catch (error) {
      // FALLBACK: Use original option if enhancement fails
      console.warn('Sub-option auto-selection failed, using original option:', error);
      processedOption = option;
    }
  }

  const existingModifierIndex = selectedModifiers.findIndex(mod => mod.name === modifierName);
  const modifier = modifiersList.find(mod => mod.name === modifierName);
  const isMultiSelect = isModifierMultiSelect(modifier);

  if (existingModifierIndex >= 0) {
    const existingModifier = selectedModifiers[existingModifierIndex];
    
    if (isMultiSelect) {
      const optionExists = existingModifier.options.some((opt: any) => opt.name === processedOption.name);
      
      if (optionExists) {
        // Remove option if already selected
        const updatedOptions = existingModifier.options.filter((opt: any) => opt.name !== processedOption.name);
        if (updatedOptions.length === 0) {
          return selectedModifiers.filter((_, index) => index !== existingModifierIndex);
        } else {
          const newModifiers = [...selectedModifiers];
          newModifiers[existingModifierIndex] = { ...existingModifier, options: updatedOptions };
          return newModifiers;
        }
      } else {
        // Add option (no maxSelection validation here)
        const newModifiers = [...selectedModifiers];
        newModifiers[existingModifierIndex] = {
          ...existingModifier,
          options: [...existingModifier.options, processedOption]
        };
        return newModifiers;
      }
    } else {
      // Single select logic
      const isSameOption = existingModifier.options.some((opt: any) => opt.name === processedOption.name);
      const isRequired = isModifierRequired(modifier);
      
      // NEW: Allow deselection ONLY for optional single-select
      if (isSameOption && !isRequired) {
        // Remove the entire modifier (deselect)
        return selectedModifiers.filter((_, index) => index !== existingModifierIndex);
      } else {
        // EXISTING: Replace selection (no change to current behavior)
        const newModifiers = [...selectedModifiers];
        newModifiers[existingModifierIndex] = { name: modifierName, options: [processedOption] };
        return newModifiers;
      }
    }
  } else {
    return [...selectedModifiers, { name: modifierName, options: [processedOption] }];
  }
};

// Additional reusable modifier utilities
export const hasEnabledOptions = (modifier: any): boolean => {
  return modifier.options && modifier.options.some((option: any) => option.isEnabled === true);
};

export const getModifierValidationMessage = (modifierName: string): string => {
  return `Please select a ${modifierName.toLowerCase()}`;
};

export const getSelectedModifierOptions = (modifierName: string, selectedModifiers: any[]): any[] => {
  const modifier = selectedModifiers.find(mod => mod.name === modifierName);
  return modifier ? modifier.options : [];
};

export const hasModifierSelection = (modifierName: string, selectedModifiers: any[]): boolean => {
  const modifier = selectedModifiers.find(mod => mod.name === modifierName);
  return modifier && modifier.options.length > 0;
};

export const hasModifierForcedStatus = (modifier: any): boolean => {
  return Boolean(modifier.is_forced);
};

/**
 * Parse max options to select (handles string and number formats)
 * Returns Infinity if not specified or invalid (unlimited selection)
 * Only applies limit if value exists and is greater than 0
 */
export const getMaxOptionsToSelect = (max?: string | number): number => {
  if (!max) return Infinity;
  
  const num = typeof max === 'number' ? max : parseFloat(max);
  
  // Only return valid positive numbers, otherwise unlimited
  return num > 0 ? num : Infinity;
};

// Sub-option utilities for Left Half/Right Half/Whole functionality
export const hasSubOptions = (option: any): boolean => {
  // Multiple safety checks
  if (!option || typeof option !== 'object') return false;
  if (option.isEnabled !== true) return false; // Main option must be enabled
  if (!option.options || !Array.isArray(option.options)) return false;
  
  // Only return true if there are actually enabled sub-options
  return option.options.some((subOpt: any) => 
    subOpt && typeof subOpt === 'object' && subOpt.isEnabled === true
  );
};

export const getSubOptions = (option: any): any[] => {
  if (!hasSubOptions(option)) return []; // Use safe detection function
  
  return option.options.filter((subOpt: any) => 
    subOpt && typeof subOpt === 'object' && subOpt.isEnabled === true
  );
};

export const createWholeOption = (mainOption: any): any | null => {
  // Don't generate if main option is not enabled
  if (!mainOption || typeof mainOption !== 'object' || mainOption.isEnabled !== true) {
    return null;
  }
  
  // Don't generate if no sub-options exist
  if (!hasSubOptions(mainOption)) {
    return null;
  }
  
  return {
    name: "Whole",
    price: mainOption.price,
    isEnabled: true,
    isDefault: true
  };
};

export const handleSubOptionSelection = (
  modifierName: string, 
  optionName: string, 
  subSelection: string, 
  subPrice: string, 
  currentModifiers: any[]
): any[] => {
  // Safety checks
  if (!modifierName || !optionName || !subSelection || !Array.isArray(currentModifiers)) {
    return currentModifiers; // Return unchanged if invalid inputs
  }
  
  return currentModifiers.map(modifier => {
    if (modifier && modifier.name === modifierName) {
      return {
        ...modifier,
        options: modifier.options?.map((option: any) => {
          if (option && option.name === optionName) {
            return {
              ...option,
              subOption: {
                selection: subSelection,
                price: subPrice
              }
            };
          }
          return option;
        }) || []
      };
    }
    return modifier;
  });
};

export const getSelectedSubOption = (
  modifierName: string, 
  optionName: string, 
  selectedModifiers: any[]
): string => {
  const modifier = selectedModifiers.find(mod => mod.name === modifierName);
  if (!modifier) return "Whole";
  
  const option = modifier.options.find((opt: any) => opt.name === optionName);
  return option?.subOption?.selection || "Whole";
};
